import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { HardHat, Loader2, Save, Plus, X } from 'lucide-react';
import { GlassCard } from '@/components/custom/GlassCard';
import { Button } from '@/components/ui/button';
import { fetchBuildTarget, saveBuildTarget, type BuildPerson } from '@/hooks/useAdminSettings';

// Admin-only screen to configure the Build hand-off for "Send to Build" (#5): the ASSIGNEE the Uniticket
// ticket is reassigned to, plus any WATCHERS added on it (so they can follow + reply to the thread). These
// are UNITICKET users identified by email — NOT app users; they never validate addresses here. The
// reassignment + watcher-adds happen via the Uniticket internal API (inert until it's configured), while
// the "Pour avis Build" comment names the assignee and lists the watchers.

const inputCls = 'mt-1 w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700';
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function BuildSettings() {
  const { t } = useTranslation('common');
  const [assignee, setAssignee] = useState<BuildPerson>({ email: '', label: '' });
  const [watchers, setWatchers] = useState<BuildPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchBuildTarget();
      if (data) {
        setAssignee(data.assignee);
        setWatchers(data.watchers ?? []);
        setConfigured(true);
      } else {
        setConfigured(false);
      }
    } catch (err: any) {
      toast.error(err?.message || t('build.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  function setWatcher(i: number, patch: Partial<BuildPerson>) {
    setWatchers((ws) => ws.map((w, idx) => (idx === i ? { ...w, ...patch } : w)));
  }
  function addWatcher() { setWatchers((ws) => [...ws, { email: '', label: '' }]); }
  function removeWatcher(i: number) { setWatchers((ws) => ws.filter((_, idx) => idx !== i)); }

  async function handleSave() {
    const a = { email: assignee.email.trim(), label: assignee.label.trim() };
    if (!a.label || !emailRe.test(a.email)) {
      toast.error(t('build.assigneeInvalid'));
      return;
    }
    // Keep only watcher rows the admin actually filled; validate each filled row.
    const cleaned: BuildPerson[] = [];
    for (const w of watchers) {
      const email = w.email.trim();
      const label = w.label.trim();
      if (!email && !label) continue; // skip empty rows
      if (!label || !emailRe.test(email)) {
        toast.error(t('build.watcherInvalid'));
        return;
      }
      cleaned.push({ email, label });
    }
    setSaving(true);
    try {
      const res = await saveBuildTarget({ assignee: a, watchers: cleaned });
      setAssignee(res.data.assignee);
      setWatchers(res.data.watchers ?? []);
      setConfigured(true);
      toast.success(t('build.saved'));
    } catch (err: any) {
      toast.error(err?.message || t('build.saveError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <HardHat className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('build.title')}</h2>
          <p className="text-sm text-slate-500">{t('build.subtitle')}</p>
        </div>
      </div>

      <GlassCard className="p-6 max-w-xl space-y-5">
        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">…</p>
        ) : (
          <>
            {!configured && (
              <div className="rounded-lg border border-dashed border-orange-300 bg-orange-50 px-3 py-2 text-xs text-orange-700">
                {t('build.currentNone')}
              </div>
            )}

            {/* Assignee */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('build.assigneeLabel')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">{t('build.fieldName')}</label>
                  <input
                    value={assignee.label}
                    onChange={(e) => setAssignee((a) => ({ ...a, label: e.target.value }))}
                    placeholder={t('build.namePlaceholder')}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">{t('build.fieldEmail')}</label>
                  <input
                    type="email"
                    value={assignee.email}
                    onChange={(e) => setAssignee((a) => ({ ...a, email: e.target.value }))}
                    placeholder={t('build.emailPlaceholder')}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>

            {/* Watchers */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('build.watchersLabel')}</p>
                <button
                  type="button"
                  onClick={addWatcher}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  <Plus className="w-3.5 h-3.5" /> {t('build.addWatcher')}
                </button>
              </div>
              {watchers.length === 0 ? (
                <p className="text-[11px] text-slate-400">{t('build.noWatchers')}</p>
              ) : (
                <div className="space-y-2">
                  {watchers.map((w, i) => (
                    <div key={i} className="flex items-end gap-2">
                      <div className="flex-1">
                        <input
                          value={w.label}
                          onChange={(e) => setWatcher(i, { label: e.target.value })}
                          placeholder={t('build.namePlaceholder')}
                          className={inputCls}
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="email"
                          value={w.email}
                          onChange={(e) => setWatcher(i, { email: e.target.value })}
                          placeholder={t('build.emailPlaceholder')}
                          className={inputCls}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeWatcher(i)}
                        className="mb-0.5 h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200"
                        aria-label={t('build.removeWatcher')}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-[11px] text-slate-400">{t('build.hint')}</p>

            <div className="pt-1">
              <Button onClick={handleSave} disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                {t('build.save')}
              </Button>
            </div>
          </>
        )}
      </GlassCard>
    </div>
  );
}
